import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicyPage.css'; /* shared legal page styling */

const TermsOfServicePage = () => {
    return (
        <div className="privacy-page">
            <div className="privacy-container">
                {/* Header */}
                <header className="privacy-header">
                    <Link to="/login" className="privacy-logo">
                        <img src="/logo.png" alt="Blastmate" />
                        <span>Blastmate</span>
                    </Link>
                    <h1>Terms of Service</h1>
                    <p className="privacy-effective">
                        <strong>Effective Date:</strong> 1 March 2026 &nbsp;|&nbsp;
                        <strong>Last Updated:</strong> 6 March 2026
                    </p>
                </header>

                {/* Content */}
                <div className="privacy-content">
                    {/* 1. Acceptance */}
                    <section className="privacy-section">
                        <h2>1. Acceptance of Terms</h2>
                        <p>
                            These Terms of Service ("Terms") constitute a legally binding agreement between you
                            ("Client", "you", "your") and <strong>Root Source Solutions Pvt Ltd</strong> ("Company", "we", "us", "our"),
                            operating as <strong>Blastmate</strong>.
                        </p>
                        <p>
                            By creating an account, accessing, or using the Blastmate platform at{' '}
                            <a href="https://blastmate.in" target="_blank" rel="noopener noreferrer">blastmate.in</a>{' '}
                            and related services (collectively, the "Service"), you agree to be bound by these Terms.
                            If you do not agree, you must not use the Service.
                        </p>
                    </section>

                    {/* 2. Service Description */}
                    <section className="privacy-section">
                        <h2>2. Service Description</h2>
                        <p>
                            Blastmate is a multi-tenant Software-as-a-Service (SaaS) platform that enables businesses to:
                        </p>
                        <ul>
                            <li>Send and receive messages via the WhatsApp Business Cloud API</li>
                            <li>Manage customer conversations from a unified dashboard</li>
                            <li>Create and send bulk broadcast campaigns</li>
                            <li>Set up automated bot responses (traditional menu-based bots)</li>
                            <li>Use AI-powered customer response automation</li>
                            <li>Build and manage a knowledge base for AI-driven answers</li>
                            <li>Track analytics and message delivery metrics</li>
                        </ul>
                        <p>
                            The Service operates as a Technology Provider under Meta's WhatsApp Business Platform
                            and is subject to Meta's Platform Terms and WhatsApp Business Messaging Policies.
                        </p>
                    </section>

                    {/* 3. Account Registration */}
                    <section className="privacy-section">
                        <h2>3. Account Registration & Eligibility</h2>
                        <ul>
                            <li>You must be at least 18 years old and a legally authorized representative of the business you register</li>
                            <li>You must provide accurate and complete business information during registration</li>
                            <li>You are responsible for maintaining the confidentiality of your login credentials</li>
                            <li>You must promptly notify us of any unauthorized access to your account</li>
                            <li>One business account per WhatsApp Business phone number is permitted</li>
                        </ul>
                    </section>

                    {/* 4. Subscription & Payment */}
                    <section className="privacy-section">
                        <h2>4. Subscription Plans & Payment</h2>

                        <h3>4.1 Plans</h3>
                        <p>
                            Blastmate offers tiered subscription plans (Starter, Business, Enterprise) with varying
                            features and message limits. During the beta period, all features may be available
                            at no charge. Plan details and pricing are available on our website and may change
                            with prior notice.
                        </p>

                        <h3>4.2 Payment Processing</h3>
                        <p>
                            Payments are processed securely via <strong>Razorpay</strong>. We accept UPI, credit/debit
                            cards, net banking, and digital wallets. By subscribing, you authorize recurring charges
                            according to your selected billing cycle (monthly or annual).
                        </p>

                        <h3>4.3 Refunds & Cancellation</h3>
                        <ul>
                            <li>You may cancel your subscription at any time from the Billing page</li>
                            <li>Upon cancellation, your access continues until the end of the current billing period</li>
                            <li>Refunds are generally not provided for partial billing periods</li>
                            <li>We reserve the right to offer prorated refunds at our discretion</li>
                        </ul>

                        <h3>4.4 Usage Limits</h3>
                        <p>
                            Each plan includes a monthly message limit. You will receive a warning at 80% usage.
                            Upon reaching 100%, outbound messaging may be paused until the next billing cycle
                            or until you upgrade your plan.
                        </p>
                    </section>

                    {/* 5. Acceptable Use */}
                    <section className="privacy-section">
                        <h2>5. Acceptable Use Policy</h2>
                        <p>You agree to use Blastmate only for lawful business purposes. You shall NOT:</p>
                        <ul>
                            <li>Send spam, unsolicited messages, or messages without valid opt-in consent from recipients</li>
                            <li>Use the Service to send messages that are illegal, fraudulent, threatening, abusive, or harassing</li>
                            <li>Violate Meta's WhatsApp Business Messaging Policy or Commerce Policy</li>
                            <li>Impersonate another person or business</li>
                            <li>Attempt to reverse-engineer, copy, or redistribute any part of the Service</li>
                            <li>Use the Service to collect or store personal data in violation of applicable privacy laws</li>
                            <li>Circumvent usage limits, rate limiting, or security measures</li>
                            <li>Share your account credentials with unauthorized parties</li>
                            <li>Use the Service to send messages related to gambling, adult content, weapons, controlled substances, or other prohibited categories under WhatsApp's policies</li>
                        </ul>
                        <p>
                            Violation of this policy may result in immediate account suspension or termination
                            without refund.
                        </p>
                    </section>

                    {/* 6. WhatsApp Compliance */}
                    <section className="privacy-section">
                        <h2>6. WhatsApp Business Compliance</h2>
                        <p>As a user of Blastmate, you acknowledge and agree that:</p>
                        <ul>
                            <li>You must comply with Meta's WhatsApp Business Messaging Policy at all times</li>
                            <li>You must obtain proper opt-in consent from customers before sending them messages</li>
                            <li>You must use only approved message templates for initiating conversations</li>
                            <li>You must respond to customer messages within the 24-hour customer service window</li>
                            <li>You are responsible for maintaining an acceptable quality rating for your WhatsApp Business Account</li>
                            <li>Meta may restrict or revoke your WhatsApp Business Account access for policy violations, and Blastmate is not responsible for such actions by Meta</li>
                        </ul>
                    </section>

                    {/* 7. Data & Privacy */}
                    <section className="privacy-section">
                        <h2>7. Data & Privacy</h2>
                        <p>
                            Your use of the Service is also governed by our{' '}
                            <Link to="/privacy-policy">Privacy Policy</Link>, which explains how we collect, use,
                            store, and protect your data. By using the Service, you consent to the data practices
                            described in the Privacy Policy.
                        </p>
                        <p>
                            You retain ownership of the content and data you upload to Blastmate (customer contacts,
                            knowledge base entries, messages). We do not claim ownership of your data.
                        </p>
                    </section>

                    {/* 8. Intellectual Property */}
                    <section className="privacy-section">
                        <h2>8. Intellectual Property</h2>
                        <p>
                            The Blastmate platform, including its design, code, features, branding, and documentation,
                            is the intellectual property of Root Source Solutions Pvt Ltd. You are granted a limited, non-exclusive,
                            non-transferable license to use the Service for your internal business purposes during
                            your active subscription.
                        </p>
                        <p>
                            You may not copy, modify, distribute, sell, or lease any part of our Service or
                            its underlying technology.
                        </p>
                    </section>

                    {/* 9. Service Availability */}
                    <section className="privacy-section">
                        <h2>9. Service Availability & Uptime</h2>
                        <p>
                            We strive to maintain high availability of the Service but do not guarantee 100% uptime.
                            The Service may be temporarily unavailable due to:
                        </p>
                        <ul>
                            <li>Scheduled maintenance (we will provide advance notice when possible)</li>
                            <li>Unplanned outages or technical issues</li>
                            <li>Third-party service disruptions (Meta, Razorpay, MongoDB Atlas, DigitalOcean)</li>
                            <li>Force majeure events</li>
                        </ul>
                        <p>
                            We are not liable for any losses arising from service interruptions beyond our
                            reasonable control.
                        </p>
                    </section>

                    {/* 10. Limitation of Liability */}
                    <section className="privacy-section">
                        <h2>10. Limitation of Liability</h2>
                        <p>
                            To the maximum extent permitted by law, Root Source Solutions Pvt Ltd shall not be liable for any
                            indirect, incidental, special, consequential, or punitive damages, including but not
                            limited to loss of revenue, loss of data, loss of business opportunities, or any
                            damages arising from:
                        </p>
                        <ul>
                            <li>Your use or inability to use the Service</li>
                            <li>Actions taken by Meta regarding your WhatsApp Business Account</li>
                            <li>Message delivery failures or delays</li>
                            <li>AI-generated responses that are incorrect or inappropriate</li>
                            <li>Unauthorized access to your account due to compromised credentials</li>
                        </ul>
                        <p>
                            Our total liability for any claim shall not exceed the amount you paid to us in the
                            12 months preceding the claim.
                        </p>
                    </section>

                    {/* 11. Termination */}
                    <section className="privacy-section">
                        <h2>11. Termination</h2>
                        <h3>11.1 By You</h3>
                        <p>
                            You may cancel your account at any time through the Billing page or by contacting us.
                            Cancellation takes effect at the end of the current billing period.
                        </p>
                        <h3>11.2 By Us</h3>
                        <p>We may suspend or terminate your account if you:</p>
                        <ul>
                            <li>Violate these Terms or the Acceptable Use Policy</li>
                            <li>Fail to pay subscription fees after due reminders</li>
                            <li>Engage in activity that harms other users or the platform</li>
                            <li>Receive repeated quality score violations from Meta</li>
                        </ul>
                        <h3>11.3 Effect of Termination</h3>
                        <p>
                            Upon termination, your access to the Service will cease. Your data will be retained
                            for 90 days after termination, during which you may request an export. After 90 days,
                            all client-specific data will be permanently deleted.
                        </p>
                    </section>

                    {/* 12. Governing Law */}
                    <section className="privacy-section">
                        <h2>12. Governing Law & Disputes</h2>
                        <p>
                            These Terms shall be governed by and construed in accordance with the laws of India.
                            Any disputes arising from these Terms or the Service shall be subject to the exclusive
                            jurisdiction of the courts in Chennai, Tamil Nadu, India.
                        </p>
                    </section>

                    {/* 13. Changes */}
                    <section className="privacy-section">
                        <h2>13. Changes to These Terms</h2>
                        <p>
                            We reserve the right to update these Terms at any time. When we make material changes,
                            we will notify you by email and update the "Last Updated" date. Continued use of the
                            Service after changes constitutes acceptance of the revised Terms.
                        </p>
                    </section>

                    {/* 14. Contact */}
                    <section className="privacy-section">
                        <h2>14. Contact Us</h2>
                        <p>For questions about these Terms, please contact us:</p>
                        <div className="privacy-contact-card">
                            <p><strong>Root Source Solutions Pvt Ltd</strong></p>
                            <p>Operating as Blastmate</p>
                            <p>Email: <a href="mailto:support@blastmate.in">support@blastmate.in</a></p>
                            <p>Website: <a href="https://blastmate.in" target="_blank" rel="noopener noreferrer">blastmate.in</a></p>
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <footer className="privacy-footer">
                    <p>&copy; {new Date().getFullYear()} Root Source Solutions Pvt Ltd. All rights reserved.</p>
                    <div className="privacy-footer-links">
                        <Link to="/privacy-policy">Privacy Policy</Link>
                        <span className="privacy-footer-sep">·</span>
                        <Link to="/login">Sign In</Link>
                        <span className="privacy-footer-sep">·</span>
                        <Link to="/signup">Create Account</Link>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default TermsOfServicePage;
